"use client";

import { useCallback } from "react";
import { useAuth, type User, type RegisterData } from "@/context/auth-context";
import { useStoreLocation } from "@/context/store-location-context";

function fullLocationFromUser(user: User) {
  if (!user.city || !user.microhub_id) return null;
  return {
    city: user.city,
    microhubId: user.microhub_id,
    addressId: user.shipping_info_id ?? undefined,
    fullAddressStr: user.full_address ?? undefined,
  };
}

function cityLocationFromUser(user: User) {
  if (!user.city || !user.microhub_id) return null;
  return {
    city: user.city,
    microhubId: user.microhub_id,
  };
}

/**
 * Wraps useAuth and automatically syncs store location whenever
 * login, register, or refreshUser returns a user with location data.
 * Use this instead of useAuth() wherever auth actions are triggered.
 */
export function useAuthWithLocationSync() {
  const auth = useAuth();
  const { login: authLogin, register: authRegister, refreshUser: authRefreshUser, logout: authLogout } = auth;
  const { setStoreLocation, clearStoreLocation, storeLocation } = useStoreLocation();

  const login = useCallback(async (email: string, password: string) => {
    const result = await authLogin(email, password);
    if (result.ok && result.user) {
      // On login, set the full location from the user profile (fresh session)
      const loc = fullLocationFromUser(result.user);
      if (loc) setStoreLocation(loc);
    }
    return result;
  }, [authLogin, setStoreLocation]);

  const register = useCallback(async (data: RegisterData) => {
    const result = await authRegister(data);
    if (result.ok && result.user) {
      // On register, set the full location from the user profile (fresh session)
      const loc = fullLocationFromUser(result.user);
      if (loc) setStoreLocation(loc);
      fbq("track", "CompleteRegistration");
    }
    return result;
  }, [authRegister, setStoreLocation]);

  const refreshUser = useCallback(async () => {
    const user = await authRefreshUser();
    if (user) {
      // On refresh, only update city/microhubId — preserve the user's explicitly chosen addressId
      const loc = storeLocation.addressId
        ? cityLocationFromUser(user)
        : fullLocationFromUser(user);
      if (loc) setStoreLocation(loc);
    }
    return user;
  }, [authRefreshUser, setStoreLocation, storeLocation.addressId]);

  const logout = useCallback(async () => {
    await authLogout();
    clearStoreLocation();
  }, [authLogout, clearStoreLocation]);

  return { ...auth, login, register, refreshUser, logout };
}
